import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSync, faPlay, faComment, faChevronDown, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { agentsApi } from '../../services/agentsApi';
import type { Ticket } from '../../types/agents';

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PLANNED: 'bg-purple-100 text-purple-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  NEEDS_HUMAN: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
};

interface Props {
  validationId: string;
}

const TicketsTab: React.FC<Props> = ({ validationId }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [feedbackTicketId, setFeedbackTicketId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const { data } = await agentsApi.listTickets(validationId);
      let parsed: Ticket[] = [];
      if (data?.content?.[0]?.text) {
        try {
          const inner = JSON.parse(data.content[0].text);
          parsed = inner.tickets || inner || [];
        } catch {
          parsed = [];
        }
      } else if (Array.isArray(data)) {
        parsed = data;
      }
      setTickets(parsed);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationId]);

  const handleExecute = async (ticketId: string) => {
    try {
      await agentsApi.triggerExecution(ticketId);
      toast.success('Ticket execution started');
      fetchTickets();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to trigger execution');
    }
  };

  const handleFeedback = async (ticketId: string) => {
    if (!feedbackText.trim()) return;
    try {
      await agentsApi.submitFeedback(ticketId, feedbackText, true);
      toast.success('Feedback submitted');
      setFeedbackTicketId(null);
      setFeedbackText('');
      fetchTickets();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to submit feedback');
    }
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <FontAwesomeIcon icon={faSync} className="animate-spin text-indigo-500 text-xl mr-3" />
        <span className="text-gray-500">Loading tickets...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end mb-2">
        <button onClick={fetchTickets} className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
          <FontAwesomeIcon icon={faSync} />
          Refresh
        </button>
      </div>

      {tickets.length === 0 ? (
        <div className="text-gray-400 py-8 text-center">No tickets yet. Use the Planner tab to create tickets.</div>
      ) : (
        tickets.map((ticket) => {
          const isExpanded = expandedId === ticket.id;
          const statusCls = STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-600';

          return (
            <div key={ticket.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Header */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-gray-400 w-3" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm">{ticket.title}</div>
                  <div className="text-xs text-gray-500">{ticket.type?.replace(/_/g, ' ')} &middot; {ticket.priority}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCls}`}>
                  {ticket.status?.replace(/_/g, ' ')}
                </span>
                {(ticket.status === 'PENDING' || ticket.status === 'PLANNED') && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleExecute(ticket.id); }}
                    className="ml-2 px-3 py-1 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700"
                    title="Execute"
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-1" />
                    Execute
                  </button>
                )}
                {ticket.status === 'NEEDS_HUMAN' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setFeedbackTicketId(ticket.id); }}
                    className="ml-2 px-3 py-1 bg-orange-500 text-white text-xs rounded-md hover:bg-orange-600"
                    title="Give Feedback"
                  >
                    <FontAwesomeIcon icon={faComment} className="mr-1" />
                    Respond
                  </button>
                )}
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="border-t border-gray-200 px-4 py-3 space-y-3 bg-gray-50">
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">Description</div>
                    <p className="text-sm text-gray-700">{ticket.description}</p>
                  </div>
                  {ticket.plan && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Execution Plan</div>
                      <pre className="text-xs bg-white p-2 rounded border border-gray-100 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {ticket.plan}
                      </pre>
                    </div>
                  )}
                  {ticket.result && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Result</div>
                      <pre className="text-xs bg-white p-2 rounded border border-gray-100 whitespace-pre-wrap max-h-48 overflow-y-auto">
                        {ticket.result}
                      </pre>
                    </div>
                  )}
                  {ticket.affectedFiles && ticket.affectedFiles.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-1">Affected Files</div>
                      <div className="flex flex-wrap gap-1">
                        {ticket.affectedFiles.map((f) => (
                          <span key={f} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-mono">{f}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {ticket.needsHumanReason && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-2">
                      <div className="text-xs font-medium text-orange-700 mb-1">Needs Input</div>
                      <p className="text-sm text-orange-800">{ticket.needsHumanReason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Feedback input */}
              {feedbackTicketId === ticket.id && (
                <div className="border-t border-gray-200 px-4 py-3 bg-orange-50">
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Provide your response..."
                    className="w-full px-3 py-2 border border-orange-300 rounded-md text-sm resize-none"
                    rows={3}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => { setFeedbackTicketId(null); setFeedbackText(''); }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleFeedback(ticket.id)}
                      className="px-3 py-1 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default TicketsTab;
